import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import type { ReactElement, Ref } from 'react'
import type { InstagramMusicPlaybackControls } from './InstagramMusicPlaybackContext'
import { clsx } from 'clsx'
import { PauseFilledIcon, PlayFilledIcon } from '@buffer-mono/popcorn'
import type { Video } from '~publish/legacy/composer/composer/entities/factories'
import styles from './InstagramMusicVideoPlayer.module.css'

type Props = {
  video: Pick<Video, 'url' | 'thumbnail'> &
    Partial<Pick<Video, 'width' | 'height'>>
  nativeControls?: boolean
  auditionId?: string
  auditionAudio?: HTMLAudioElement
  onAuditionEnded?: () => void
  onAuditionError?: () => void
  className?: string
  videoVolume?: number
  soundtrackSrc?: string | null
  hasSoundtrack?: boolean
  soundtrackVolume?: number
  onPlayingChange?: (isPlaying: boolean) => void
  onPlaybackErrorChange?: (failed: boolean) => void
  playbackRef?: Ref<InstagramMusicPlaybackControls>
}

function toMediaVolume(value: number | undefined): number {
  return value === undefined || Number.isNaN(value)
    ? 1
    : Math.min(1, Math.max(0, value / 100))
}

export function InstagramMusicVideoPlayer({
  video,
  nativeControls = false,
  auditionId,
  auditionAudio,
  onAuditionEnded,
  onAuditionError,
  className,
  videoVolume,
  soundtrackSrc,
  hasSoundtrack = Boolean(soundtrackSrc),
  soundtrackVolume,
  onPlayingChange,
  onPlaybackErrorChange,
  playbackRef,
}: Props): ReactElement {
  const videoRef = useRef<HTMLVideoElement>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const videoWaitingRef = useRef(false)
  const playAttempt = useRef(0)
  const [showControls, setShowControls] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  useEffect(() => {
    if (!auditionAudio) return
    audioRef.current = auditionAudio
    videoWaitingRef.current = true
    return (): void => {
      auditionAudio.pause()
      if (audioRef.current === auditionAudio) audioRef.current = null
    }
  }, [auditionAudio])

  const pauseAudio = (): void => {
    audioRef.current?.pause()
    onPlayingChange?.(false)
  }

  const resumeAudio = useCallback((): void => {
    const source = videoRef.current
    const audio = audioRef.current
    if (!source || !audio) return
    audio.muted = source.muted
    audio.playbackRate = source.playbackRate
    if (!source.paused && !source.seeking && !videoWaitingRef.current) {
      void audio.play().then(
        () => {
          if (
            audioRef.current !== audio ||
            source.paused ||
            source.seeking ||
            videoWaitingRef.current
          )
            return
          onPlaybackErrorChange?.(false)
          return onPlayingChange?.(true)
        },
        () => {
          if (audioRef.current !== audio) return
          onPlayingChange?.(false)
          if (!source.paused && !source.seeking && !videoWaitingRef.current) {
            if (auditionAudio) onAuditionError?.()
            else onPlaybackErrorChange?.(true)
          }
        },
      )
    }
  }, [onPlayingChange, onPlaybackErrorChange, auditionAudio, onAuditionError])

  const syncAudio = useCallback((): void => {
    const source = videoRef.current
    const audio = audioRef.current
    if (!source || !audio) return
    if (audio.currentTime !== source.currentTime) {
      audio.currentTime = source.currentTime
    }
    resumeAudio()
  }, [resumeAudio])

  useEffect(() => {
    if (!auditionAudio) return
    auditionAudio.addEventListener('loadedmetadata', syncAudio)
    auditionAudio.addEventListener('canplay', resumeAudio)
    return (): void => {
      auditionAudio.removeEventListener('loadedmetadata', syncAudio)
      auditionAudio.removeEventListener('canplay', resumeAudio)
    }
  }, [auditionAudio, syncAudio, resumeAudio])

  useEffect(() => {
    if (videoRef.current) videoRef.current.volume = toMediaVolume(videoVolume)
  }, [videoVolume])

  useEffect(() => {
    if (audioRef.current && !auditionAudio)
      audioRef.current.volume = toMediaVolume(soundtrackVolume)
  }, [soundtrackSrc, soundtrackVolume, auditionAudio])

  useEffect(() => {
    const audio = auditionAudio ? null : audioRef.current
    return (): void => audio?.pause()
  }, [soundtrackSrc, auditionAudio])

  useEffect(() => {
    playAttempt.current += 1
    return (): void => {
      playAttempt.current += 1
    }
  }, [video.url, soundtrackSrc])

  useEffect(() => {
    onPlaybackErrorChange?.(false)
    if (soundtrackSrc) syncAudio()
    else onPlayingChange?.(false)
  }, [soundtrackSrc, syncAudio, onPlayingChange, onPlaybackErrorChange])

  useEffect(() => {
    const source = videoRef.current
    if (!auditionId || !source) return
    source.currentTime = 0
    videoWaitingRef.current = true
    source.muted = false
    setShowControls(true)
    playAttempt.current += 1
    let active = true
    void source.play().catch(() => {
      if (active) {
        setShowControls(false)
        onAuditionError?.()
      }
    })
    return (): void => {
      active = false
      playAttempt.current += 1
      source.pause()
    }
  }, [auditionId, video.url, onAuditionError])

  const playVideo = (): void => {
    const source = videoRef.current
    if (!source) return
    source.muted = false
    setShowControls(true)
    const attempt = ++playAttempt.current
    void source.play().catch(() => {
      if (playAttempt.current !== attempt || videoRef.current !== source) return
      setShowControls(false)
      if (playbackRef) onPlaybackErrorChange?.(true)
    })
  }

  useImperativeHandle(playbackRef, () => ({
    toggle: (): void => {
      const source = videoRef.current
      if (!source) return
      if (source.paused) playVideo()
      else {
        playAttempt.current += 1
        source.pause()
        pauseAudio()
      }
    },
    pause: (): void => {
      playAttempt.current += 1
      videoRef.current?.pause()
      pauseAudio()
    },
  }))

  return (
    <div className={clsx(styles.player, className)} data-testid="preview-video">
      {/* Uploaded media has no separate caption track. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        aria-label="video-player"
        data-testid="video-player"
        poster={video.thumbnail}
        width={nativeControls ? video.width : undefined}
        height={nativeControls ? video.height : undefined}
        src={video.url}
        preload="none"
        muted={!nativeControls}
        controls={nativeControls || (showControls && !hasSoundtrack)}
        playsInline={nativeControls || undefined}
        crossOrigin={nativeControls ? undefined : 'anonymous'}
        onPointerDown={(event) => {
          // Keep Safari's native seek bar gestures out of the lightbox swipe handler.
          if (
            nativeControls &&
            /^((?!chrome|android).)*(safari|mobile)/i.test(
              navigator.userAgent,
            ) &&
            event.currentTarget.getBoundingClientRect().bottom - event.clientY <
              40
          ) {
            event.stopPropagation()
          }
        }}
        onPlaying={() => {
          setIsVideoPlaying(true)
          videoWaitingRef.current = false
          syncAudio()
        }}
        onPause={() => {
          setIsVideoPlaying(false)
          playAttempt.current += 1
          pauseAudio()
        }}
        onWaiting={() => {
          videoWaitingRef.current = true
          pauseAudio()
        }}
        onSeeking={() => {
          playAttempt.current += 1
          pauseAudio()
        }}
        onSeeked={syncAudio}
        onRateChange={syncAudio}
        onVolumeChange={() => {
          if (audioRef.current && videoRef.current)
            audioRef.current.muted = videoRef.current.muted
        }}
        onEnded={() => {
          setIsVideoPlaying(false)
          pauseAudio()
          if (audioRef.current) audioRef.current.currentTime = 0
          setShowControls(false)
          onAuditionEnded?.()
        }}
        onError={() => {
          pauseAudio()
          onAuditionError?.()
        }}
      />
      {soundtrackSrc && (
        // Meta supplies music previews without caption tracks.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={audioRef}
          src={soundtrackSrc}
          hidden
          preload="none"
          aria-label="Instagram audio preview"
          onLoadedMetadata={syncAudio}
          onCanPlay={resumeAudio}
          onEnded={() => onPlayingChange?.(false)}
          onError={() => {
            onPlaybackErrorChange?.(true)
            onPlayingChange?.(false)
          }}
        />
      )}
      {!nativeControls && (!showControls || hasSoundtrack) && (
        <button
          type="button"
          className={styles.playbackButton}
          data-playing={isVideoPlaying}
          onClick={(): void => {
            if (isVideoPlaying) {
              playAttempt.current += 1
              videoRef.current?.pause()
              pauseAudio()
            } else playVideo()
          }}
          aria-label={
            isVideoPlaying ? 'Pause Reel preview' : 'Play Reel preview'
          }
        >
          {isVideoPlaying ? (
            <PauseFilledIcon size="large" />
          ) : (
            <PlayFilledIcon size="large" />
          )}
        </button>
      )}
      <div className="overlay" />
    </div>
  )
}
